// apps/web/src/components/content/ContentLibrary.tsx
// Complete content library management

import type React from 'react';
import { useState, useEffect } from 'react';
import type { ContentItem } from '../../types/content';
import { apiClient } from '../../lib/apiClient';

export function ContentLibrary() {
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/content');
        setContentItems(response.data.content);
      } catch (err) {
        setError('Failed to load content');
        console.error('Error loading content:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  const handleUploadContent = async (contentData: any) => {
    try {
      const response = await apiClient.post('/api/v1/content', contentData);
      setContentItems([response.data, ...contentItems]);
      setShowUploadForm(false);
    } catch (err) {
      setError('Failed to upload content');
      console.error('Error uploading content:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading content...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="content-library">
      <div className="header">
        <h2>Content Library</h2>
        <button 
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="btn btn-primary"
        >
          {showUploadForm ? 'Cancel' : 'Upload Content'}
        </button>
      </div>

      {showUploadForm && (
        <ContentUploadForm 
          onSubmit={handleUploadContent} 
          onCancel={() => setShowUploadForm(false)} 
        />
      )}

      <div className="content-grid">
        {contentItems.map(item => (
          <ContentCard key={item.id} content={item} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function ContentCard({ content }: { content: ContentItem }) {
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'IMAGE': return '🖼️';
      case 'VIDEO': return '🎬';
      case 'DOCUMENT': return '📄';
      case 'AUDIO': return '🎵';
      default: return '📁';
    }
  };

  return (
    <div className="content-card">
      <div className="content-header">
        <span className="content-icon">{getTypeIcon(content.contentType)}</span>
        <h3>{content.title}</h3>
      </div>
      <p>{content.description || 'No description'}</p>
      <div className="content-meta">
        <span>{content.fileSize ? `${(content.fileSize / 1024).toFixed(1)} KB` : 'N/A'}</span>
        <span>{content.contentType}</span>
        <span>{new Date(content.createdAt).toLocaleDateString()}</span>
      </div>
      <div className="actions">
        <button className="btn btn-secondary">Preview</button>
        <button className="btn btn-outline">Edit</button>
        <button className="btn btn-danger">Delete</button>
      </div>
    </div>
  );
}

// Helper component
function ContentUploadForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    contentType: 'IMAGE',
    isPublic: false
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="content-upload-form">
      <h3>Upload New Content</h3>
      <input
        type="text"
        placeholder="Title"
        value={formData.title}
        onChange={(e) => setFormData({...formData, title: e.target.value})}
        required
      />
      <textarea
        placeholder="Description"
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
      <select
        value={formData.contentType}
        onChange={(e) => setFormData({...formData, contentType: e.target.value})}
      >
        <option value="IMAGE">Image</option>
        <option value="VIDEO">Video</option>
        <option value="DOCUMENT">Document</option>
        <option value="AUDIO">Audio</option>
        <option value="OTHER">Other</option>
      </select>
      <label>
        <input
          type="checkbox"
          checked={formData.isPublic}
          onChange={(e) => setFormData({...formData, isPublic: e.target.checked})}
        />
        Make Public
      </label>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Upload</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}